import React from 'react';

import images from 'assets/images';
import PageLayout from 'components/common/PageLayout';
import ScrollSection from 'components/common/ScrollSection';
import { ENTERPRISE_CONTACT_LINK, SUBSCRIPTION_PLANS_PAGE } from 'components/constants';

import styles from './styles.module.scss';

const flowVideos = [
  {
    title: 'Personalize from the first screen',
    desc: 'Use information collected earlier in the journey to make every next step more relevant, personal and useful.',
    videoSrc: {
      desktop: 'https://assts.stories.sc/website/desktop/get_personal.mp4',
      mobile: 'https://assts.stories.sc/website/mobile/get_personal.mp4',
    },
    poster: { desktop: 'https://assts.stories.sc/website/poster/get_personal.jpg' },
  },
  {
    title: 'Let every answer shape the outcome',
    desc: 'Score choices and interactions as the journey unfolds, then use the accumulated signal to recommend the right result or next action.',
    videoSrc: {
      desktop: 'https://assts.stories.sc/website/desktop/scoring.mp4',
      mobile: 'https://assts.stories.sc/website/mobile/scoring.mp4',
    },
    poster: { desktop: 'https://assts.stories.sc/website/poster/scoring.jpg' },
  },
  {
    title: 'Carry context into the journey',
    desc: 'Pass known information through URL parameters so each user can enter the right path without answering the same questions again.',
    videoSrc: {
      desktop: 'https://assts.stories.sc/website/desktop/url_parameters.mp4',
      mobile: 'https://assts.stories.sc/website/mobile/url_parameters.mp4',
    },
    poster: { desktop: 'https://assts.stories.sc/website/poster/url_parameters.jpg' },
  },
  {
    title: 'Branch without rebuilding',
    desc: 'Use conditional logic to reveal, skip or redirect steps based on answers, scores and data already known about the user.',
    videoSrc: {
      desktop: 'https://assts.stories.sc/website/desktop/flowcondtion.mp4',
      mobile: 'https://assts.stories.sc/website/mobile/flowcondtion.mp4',
    },
    poster: { desktop: 'https://assts.stories.sc/website/poster/flowcondtion.jpg' },
  },
];

const capabilities = [
  {
    title: 'Conditional paths',
    text: 'Branch, skip and redirect steps according to answers, events, scores or known user data.',
  },
  {
    title: 'Scoring and outcomes',
    text: 'Assign points to answers and use the combined score to show the right result, content or next step.',
  },
  {
    title: 'Dynamic fields',
    text: 'Reuse names, roles and other captured information in content, questions and calls to action.',
  },
  {
    title: 'URL parameters',
    text: 'Pass known details such as names, IDs or campaign data into the journey from the first screen.',
  },
  {
    title: 'Forms and validation',
    text: 'Build forms with required fields and validation to collect accurate, structured first-party data.',
  },
  {
    title: 'Mixed card journeys',
    text: 'Combine content, quizzes, polls, yes-or-no questions and lead forms in one connected flow.',
  },
  {
    title: 'Participation controls',
    text: 'Set an interaction to allow one response per user or repeated participation.',
  },
  {
    title: 'Shareable results',
    text: 'Let users share the personalized result or outcome they receive at the end of the flow.',
  },
  {
    title: 'Event tracking',
    text: 'Track selected clicks, form submissions, answers and navigation events throughout the journey.',
  },
  {
    title: 'A/B testing',
    text: 'Compare flow alternatives and use performance data to improve individual steps and outcomes.',
  },
  {
    title: 'Connected follow-ups',
    text: 'Send data to your stack, trigger a follow-up or route the user into the right business process.',
  },
  {
    title: 'Multi-device experiences',
    text: 'Adapt layouts and content for mobile, tablet and desktop while keeping one connected journey.',
  },
];

const useCases = [
  { title: 'Product advisors', text: 'Understand needs and guide each customer toward the right product, service or plan.' },
  { title: 'Lead qualification', text: 'Capture intent and context before routing every lead to the most relevant next step.' },
  { title: 'Onboarding journeys', text: 'Guide users through information, decisions and required inputs without unnecessary steps.' },
  { title: 'Assessments and quizzes', text: 'Combine questions, scoring and personalized feedback in one complete, measurable journey.' },
];

const Flow: React.FC = () => (
  <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>Visual flow engine</p>
            <h1>Build journeys that adapt, convert and improve.</h1>
            <p className={styles.heroText}>
              Connect content, questions, forms and logic into intelligent flows. Guide every user toward the right
              outcome, capture structured first-party data and see exactly where the journey can perform better.
            </p>
            <div className={styles.heroActions}>
              <a className={styles.primaryCta} href={SUBSCRIPTION_PLANS_PAGE} target="_blank" rel="noopener noreferrer">
                Start creating <span aria-hidden="true">→</span>
              </a>
              <a className={styles.textCta} href="#flow-in-action">See flows in action</a>
            </div>
          </div>

          <div className={styles.heroSummary}>
            <span>One connected journey</span>
            <div>
              <strong>Engage</strong><i aria-hidden="true">→</i>
              <strong>Understand</strong><i aria-hidden="true">→</i>
              <strong>Personalize</strong><i aria-hidden="true">→</i>
              <strong>Complete</strong>
            </div>
          </div>
        </div>

        <figure className={styles.heroVisual}>
          <img src={images.flowImage} alt="A conditional Storycards flow connecting interactive cards" />
        </figure>
      </section>

      <section className={styles.definition}>
        <div className={styles.sectionInner}>
          <h2>The logic layer behind every interactive product.</h2>
          <p>
            Cards give every screen a purpose. Flow connects those screens into an adaptive journey. Define what
            happens next, use each interaction to make the experience smarter and keep the entire path visible,
            measurable and easy to change.
          </p>
        </div>
      </section>

      <section className={styles.videoStory} id="flow-in-action">
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>Flow in action</p>
              <h2>Every journey can respond in real time.</h2>
            </div>
            <p>
              Combine known data with choices made along the way. The path, content and outcome can change for every
              user while the experience remains one manageable product.
            </p>
          </header>
          <div className={styles.videoScroll}>
            <ScrollSection sectionData={flowVideos} />
          </div>
        </div>
      </section>

      <section className={styles.performance}>
        <div className={styles.sectionInner}>
          <div className={styles.performanceLayout}>
            <div className={styles.performanceCopy}>
              <p className={styles.eyebrow}>Built to improve</p>
              <h2>See where people continue and where they leave.</h2>
              <p>
                Measure engagement, completion and drop-off at every step. Compare paths and versions, identify the
                point where momentum is lost and improve the journey without rebuilding it.
              </p>
            </div>

            <div className={styles.flowReport} aria-label="Illustrative step-level flow performance">
              <div className={styles.reportHeader}><span>Flow performance</span><em>Live</em></div>
              <div className={styles.reportRow}><div><span>01</span><strong>Started</strong></div><i><b style={{ width: '100%' }} /></i><em>100%</em></div>
              <div className={styles.reportRow}><div><span>02</span><strong>Engaged</strong></div><i><b style={{ width: '82%' }} /></i><em>82%</em></div>
              <div className={styles.reportRow}><div><span>03</span><strong>Qualified</strong></div><i><b style={{ width: '68%' }} /></i><em>68%</em></div>
              <div className={styles.reportRow}><div><span>04</span><strong>Completed</strong></div><i><b style={{ width: '57%' }} /></i><em>57%</em></div>
              <small>Illustrative journey view</small>
            </div>
          </div>

          <div className={styles.performancePoints}>
            <article><span>01</span><h3>Measure every step</h3><p>See views, answers, interactions, completion and drop-off across the full path.</p></article>
            <article><span>02</span><h3>Understand every path</h3><p>Compare how different audiences move through branches, scores and personalized routes.</p></article>
            <article><span>03</span><h3>Improve continuously</h3><p>Test alternatives and refine individual steps while the rest of the product stays live.</p></article>
          </div>
        </div>
      </section>

      <section className={styles.capabilities}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2>Flow features for every part of the journey.</h2>
            <p>Build the logic visually, control how people participate and connect each interaction to measurable data and next actions.</p>
          </div>
          <div className={styles.capabilityGrid}>
            {capabilities.map((capability, index) => (
              <article key={capability.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{capability.title}</h3>
                <p>{capability.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.useCases}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2>Journeys built around a real outcome.</h2>
            <p>Use the same engine for customer, audience and employee experiences across the organization.</p>
          </div>
          <div className={styles.useCaseGrid}>
            {useCases.map((useCase) => (
              <article key={useCase.title}><h3>{useCase.title}</h3><p>{useCase.text}</p></article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.cta}>
        <div className={styles.ctaInner}>
          <h2>Build a journey that gets better with every interaction.</h2>
          <div>
            <p>Turn content, logic and first-party data into adaptive digital products your teams can keep improving.</p>
            <div className={styles.ctaActions}>
              <a className={styles.primaryCta} href={SUBSCRIPTION_PLANS_PAGE} target="_blank" rel="noopener noreferrer">
                Start creating <span aria-hidden="true">→</span>
              </a>
              <a className={styles.textCta} href={ENTERPRISE_CONTACT_LINK}>Talk to our enterprise team</a>
            </div>
          </div>
        </div>
      </section>
    </main>
  </PageLayout>
);

export default Flow;
